"use client";

import { useEffect, useState, useSyncExternalStore } from "react";
import { AppHeader } from "@/components/layout/app-header";
import { usePrefs } from "@/components/providers/app-providers";
import { listPublicApiCommands } from "@/lib/public-api/registry";

const noopSubscribe = () => () => {};

export default function PublicApiDocsPage() {
  const { t } = usePrefs();
  const commands = listPublicApiCommands();
  const origin = useSyncExternalStore(
    noopSubscribe,
    () => window.location.origin,
    () => "https://your-host",
  );
  const [userEmail, setUserEmail] = useState<string>();

  useEffect(() => {
    fetch("/api/auth/me")
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => data?.user?.email && setUserEmail(data.user.email))
      .catch(() => {});
  }, []);

  return (
    <div className="flex min-h-full flex-1 flex-col">
      <AppHeader userEmail={userEmail} />

      <main className="mx-auto w-full max-w-4xl flex-1 px-4 py-8 sm:px-6 sm:py-10">
        <div className="space-y-2">
          <p className="text-sm font-semibold tracking-wide text-teal-700 dark:text-teal-400">
            HTTP API
          </p>
          <h1 className="font-[family-name:var(--font-manrope)] text-2xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-3xl">
            {t("apiDocsTitle")}
          </h1>
          <p className="max-w-2xl text-sm text-slate-600 dark:text-slate-400">{t("apiDocsIntro")}</p>
        </div>

        <section className="mt-8 space-y-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-6">
          <h2 className="font-semibold text-slate-900 dark:text-slate-50">{t("apiBaseTitle")}</h2>
          <p className="text-sm text-slate-600 dark:text-slate-400">{t("apiBaseBody")}</p>
          <pre className="overflow-x-auto rounded-xl bg-slate-950 p-4 text-sm text-slate-100" dir="ltr">
            {`POST ${origin}/api/{userUid}/{deviceUid}
Content-Type: application/json

{
  "command": "<commandName>",
  "params": { ... }
}`}
          </pre>
          <p className="text-sm text-slate-600 dark:text-slate-400">{t("apiUidHint")}</p>
        </section>

        <section className="mt-8 space-y-4">
          <h2 className="font-semibold text-slate-900 dark:text-slate-50">{t("apiCommandsTitle")}</h2>
          {commands.map((cmd) => (
            <article
              key={cmd.name}
              className="space-y-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-6"
            >
              <div className="flex flex-wrap items-baseline gap-3">
                <h3
                  className="rounded-md bg-teal-50 px-2 py-0.5 font-mono text-sm font-semibold text-teal-700 dark:bg-teal-950/60 dark:text-teal-300"
                  dir="ltr"
                >
                  {cmd.name}
                </h3>
                <span className="text-sm text-slate-600 dark:text-slate-400">{cmd.summary}</span>
              </div>
              <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                {cmd.description}
              </p>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                {t("apiExample")}
              </p>
              <pre className="overflow-x-auto rounded-xl bg-slate-950 p-4 text-sm text-slate-100" dir="ltr">
                {`curl -X POST ${origin}/api/u_YOUR_USER_UID/m_YOUR_DEVICE_UID \\
  -H "Content-Type: application/json" \\
  -d '${JSON.stringify(cmd.exampleBody)}'`}
              </pre>
            </article>
          ))}
        </section>
      </main>
    </div>
  );
}
